import { useMemo } from 'react'

import type { DateRange } from 'react-day-picker'
import { useInfiniteQuery } from 'react-query'

import { fetchVideos } from '../data/service'
import type { SocialAccount, SocialMediaType } from '../types'
import { useExportSocial } from './use-export'

const FETCH_SIZE = 10

export const useSocialPagination = ({
  account,
  socialMediaType,
  dateRange,
  creatorId,
  sortOption,
}: {
  account: SocialAccount
  socialMediaType: SocialMediaType
  dateRange: DateRange | undefined
  creatorId: number | undefined
  sortOption: string
}) => {
  const { setFilledAccountsMap } = useExportSocial()
  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isLoading } =
    useInfiniteQuery(
      [creatorId, socialMediaType, account.account, dateRange, sortOption],
      async ({ pageParam = 1 }) =>
        fetchVideos(
          creatorId,
          socialMediaType,
          account.account,
          pageParam,
          sortOption,
          dateRange!,
        ),
      {
        getNextPageParam: (lastPage, pages) => {
          const maxPages = Math.ceil(lastPage.total / FETCH_SIZE)
          const nextPage = pages.length + 1

          if (nextPage <= maxPages) {
            return nextPage
          }

          return undefined
        },
        onSuccess(data) {
          setFilledAccountsMap((prev) => ({
            ...prev,
            [account.account]: data.pages[0]?.total > 0,
          }))
        },
        refetchOnMount: true,
      },
    )

  return useMemo(
    () => ({
      videos: data,
      handleLoadMore: fetchNextPage,
      isFetchingNextPage,
      isInitiallyLoading: isLoading,
      hasNextPage,
    }),
    [data, fetchNextPage, isFetchingNextPage, hasNextPage, isLoading],
  )
}
